import React, { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { useJsonLd } from '../hooks/useJsonLd'
import { buildContactPageGraph } from '../lib/schema'
import { CONTACT_WIDGET_URL } from '../constants/external-links'
import LocalAnswerBlock from '../components/LocalAnswerBlock.jsx'

const Contact = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const location = useLocation()
  const [widgetLoaded, setWidgetLoaded] = useState(false)

  usePageSeo({
    enabled: !isLoading,
    title: t('contact.meta.title'),
    description: t('contact.meta.description'),
    keywords: t('contact.meta.keywords'),
    pathname: location.pathname,
  })

  const contactSchema = useMemo(
    () =>
      buildContactPageGraph({
        pathname: location.pathname,
        name: t('contact.meta.title'),
        description: t('contact.meta.description'),
      }),
    [location.pathname, t, isLoading]
  )

  useJsonLd('data-contact-schema', contactSchema, !isLoading)

  if (isLoading) {
    return (
      <div className="bg-primary-offWhite min-h-[60vh] py-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
          <div className="animate-pulse grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="hidden lg:block lg:col-span-4 h-96 bg-gray-200 rounded-2xl" />
            <div className="lg:col-span-8 h-96 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-primary-offWhite">
      <LocalAnswerBlock compact />
      <section className="py-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
          <div className="lg:hidden mb-8">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/img/lidiana/LidianaPortraitSitting.jpg"
                alt="Contact VIVA Psychotherapy"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-darkGreen/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-xl font-bold text-white mb-2">{t('contact.form.imageTitle')}</h3>
                <p className="text-white/90 text-sm">{t('contact.form.imageDescription')}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="order-2 lg:order-1 lg:col-span-4 hidden lg:block min-w-0">
              <div className="sticky top-8 max-w-md mx-auto lg:mx-0">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="/img/lidiana/LidianaPortraitSitting.jpg"
                    alt="Contact VIVA Psychotherapy"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-darkGreen/30 to-transparent" />
                  <div className="absolute bottom-8 left-8 right-8">
                    <h3 className="text-2xl font-bold text-white mb-2">{t('contact.form.imageTitle')}</h3>
                    <p className="text-white/90">{t('contact.form.imageDescription')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 lg:col-span-8 min-w-0 w-full">
              <div className="mb-6 text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl font-bold text-primary-darkGreen mb-3">
                  {t('contact.form.widgetTitle')}
                </h1>
                <p className="text-gray-700 text-lg">{t('contact.form.subtitle')}</p>
              </div>
              {/* SimplePractice widget renders its own card on a light-gray page that blends
                  with bg-primary-offWhite, so no wrapper card here (avoids a card-in-card look).
                  Fixed height covers the tallest step (contact details); spare space is invisible. */}
              <div className="relative w-full">
                {!widgetLoaded && (
                  <div className="absolute inset-x-0 top-0 animate-pulse bg-white rounded-lg shadow h-[480px]" aria-hidden />
                )}
                <iframe
                  src={CONTACT_WIDGET_URL}
                  title={t('contact.form.widgetTitle')}
                  onLoad={() => setWidgetLoaded(true)}
                  className={`block w-full h-[900px] border-0 bg-transparent transition-opacity duration-300 ${widgetLoaded ? 'opacity-100' : 'opacity-0'}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-primary-golden rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary-darkGreen mb-4">{t('contact.methods.phone.title')}</h3>
              <p className="text-gray-700 mb-4">{t('contact.methods.phone.description')}</p>
              <a href="tel:+15617597519" className="text-primary-darkGreen font-semibold hover:underline">
                (561) 759-7519
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-primary-golden rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary-darkGreen mb-4">{t('contact.methods.email.title')}</h3>
              <p className="text-gray-700 mb-4">{t('contact.methods.email.description')}</p>
              <a href="mailto:info@vivapsychotherapy.com" className="text-primary-darkGreen font-semibold hover:underline break-all">
                info@vivapsychotherapy.com
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-primary-golden rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary-darkGreen mb-4">{t('contact.methods.location.title')}</h3>
              <p className="text-gray-700 mb-4">{t('contact.methods.location.description')}</p>
              <p className="text-primary-darkGreen font-semibold">East Boca Raton, FL</p>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-primary-golden rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-primary-darkGreen mb-4">{t('contact.methods.hours.title')}</h3>
              <p className="text-gray-700 mb-4">{t('contact.methods.hours.description')}</p>
              <p className="text-primary-darkGreen font-semibold">Mon-Fri: 9AM-6PM</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
